import React, { useState } from 'react';
import {
  Container,
  Grid,
  Box,
  Typography,
  Fade
} from '@mui/material';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useSectionVisibility } from '../../../hooks';
import {
  colors,
  containerVariants,
  itemVariants
} from '../../styled/StyledComponents';
import TeamMemberCard from './TeamMemberCard';
import TeamMemberDialog from './TeamMemberDialog';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import { styled, keyframes } from '@mui/material/styles';

// Animations
const shimmer = keyframes`
  0% { background-position: -200% center; }
  100% { background-position: 200% center; }
`;

// Styled components
const SectionWrapper = styled(Box)(({ theme }) => ({
  padding: theme.spacing(8, 0),
  background: `linear-gradient(135deg, ${colors.primary}05 0%, ${colors.background} 50%, ${colors.primary}05 100%)`,
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '1px',
    background: `linear-gradient(90deg, transparent, ${colors.primary}30, transparent)`,
    backgroundSize: '200% 100%',
    animation: `${shimmer} 4s linear infinite`,
  },
  [theme.breakpoints.up('md')]: {
    padding: theme.spacing(12, 0),
  },
}));

const TeamSection = styled(Box)(({ theme }) => ({
  position: 'relative',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: '50%',
    transform: 'translateX(-50%)',
    width: '80%',
    height: '1px',
    background: `linear-gradient(90deg, transparent, ${colors.primary}30, transparent)`,
    [theme.breakpoints.up('md')]: {
      display: 'none',
    },
  },
}));

const AboutSection = () => {
  const [ref, inView] = useSectionVisibility({ threshold: 0.1 });
  const { t } = useTranslation();
  const [selectedMember, setSelectedMember] = useState(null);

  const teamMembers = t('landing.about.team.members', { returnObjects: true });

  const contactInfo = [
    {
      icon: <LocationOnIcon sx={{ fontSize: 24 }} />,
      title: t('landing.contact.info.0.title'),
      content: t('landing.contact.info.0.content'),
      color: colors.primary,
    },
    {
      icon: <EmailIcon sx={{ fontSize: 24 }} />,
      title: t('landing.contact.info.1.title'),
      content: t('landing.contact.info.1.content'),
      color: colors.secondary,
    },
    {
      icon: <PhoneIcon sx={{ fontSize: 24 }} />,
      title: t('landing.contact.info.2.title'),
      content: t('landing.contact.info.2.content'),
      color: colors.tertiary,
    },
  ];

  return (
    <SectionWrapper
      ref={ref}
      id="about-section"
    >
      <Container maxWidth="xl">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
        >
          <Grid container spacing={{ xs: 6, md: 8 }} alignItems="center">
            <Grid item xs={12} md={6}>
              <motion.div variants={itemVariants}>
                <Typography
                  variant="overline"
                  sx={{
                    color: colors.primary,
                    fontWeight: 600,
                    letterSpacing: 2,
                    mb: 1,
                    display: 'inline-block',
                    position: 'relative',
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      bottom: -4,
                      left: '25%',
                      width: '50%',
                      height: '2px',
                      background: `linear-gradient(90deg, transparent, ${colors.primary}, transparent)`,
                    }
                  }}
                >
                  {t('landing.about.header')}
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    color: colors.text,
                    fontWeight: 800,
                    mb: 3,
                    fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem', lg: '3.5rem' },
                    lineHeight: 1.2,
                  }}
                >
                  {t('landing.about.title')}
                </Typography>
                <Typography
                  variant="h5"
                  sx={{
                    color: colors.textLight,
                    mb: 4,
                    lineHeight: 1.8,
                    fontSize: { xs: '1rem', sm: '1.1rem', md: '1.25rem' },
                  }}
                >
                  {t('landing.about.description')}
                </Typography>

                <Grid container spacing={4} sx={{ mt: 6 }}>
                  {contactInfo.map((info, index) => (
                    <Grid item xs={12} sm={4} key={index}>
                      <Fade 
                        in={inView} 
                        timeout={1000} 
                        style={{ transitionDelay: `${200 + index * 150}ms` }}
                      >
                        <Box sx={{ 
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: 2
                        }}>
                          <Box sx={{
                            width: 44,
                            height: 44,
                            minWidth: 44,
                            flexShrink: 0,
                            borderRadius: '50%',
                            border: `2px solid ${info.color}`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: info.color
                          }}>
                            {info.icon}
                          </Box>
                          <Box>
                            <Typography
                              variant="subtitle2"
                              sx={{
                                color: colors.text,
                                fontWeight: 700,
                                mb: 0.5,
                              }}
                            >
                              {info.title}
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                color: colors.textLight,
                                fontSize: '0.9rem',
                              }}
                            >
                              {info.content}
                            </Typography>
                          </Box>
                        </Box>
                      </Fade>
                    </Grid>
                  ))}
                </Grid>
              </motion.div>
            </Grid>

            <Grid item xs={12} md={6}>
              <TeamSection>
                <motion.div variants={itemVariants}>
                  <Typography
                    variant="h4"
                    sx={{
                      color: colors.text,
                      fontWeight: 700,
                      mb: 4,
                      position: 'relative',
                      display: 'inline-block',
                      '&::after': {
                        content: '""',
                        position: 'absolute',
                        bottom: -8,
                        left: 0,
                        width: '40%',
                        height: '3px',
                        background: colors.primary,
                        borderRadius: '2px',
                      }
                    }}
                  >
                    {t('landing.about.team.title')}
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    {Array.isArray(teamMembers) && teamMembers.map((member, index) => (
                      <Fade
                        key={index}
                        in={inView}
                        timeout={1000}
                        style={{ transitionDelay: `${200 + index * 150}ms` }}
                      >
                        <Box>
                          <TeamMemberCard
                            member={member}
                            onClick={() => setSelectedMember(member)}
                            index={index}
                          />
                        </Box>
                      </Fade>
                    ))}
                    <Typography
                      variant="body2"
                      sx={{
                        color: colors.textLight,
                        fontStyle: 'italic',
                        mt: 1,
                      }}
                    >
                      {t('landing.about.teamNote')}
                    </Typography>
                  </Box>
                </motion.div>
              </TeamSection>
            </Grid>
          </Grid>
        </motion.div>
      </Container>

      <TeamMemberDialog
        member={selectedMember}
        open={Boolean(selectedMember)}
        onClose={() => setSelectedMember(null)}
      />
    </SectionWrapper>
  );
};

export default AboutSection;
